import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase';
import { format } from 'date-fns';
import Papa from 'papaparse';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiDownload, FiCalendar, FiUsers, FiMessageCircle, FiTrendingUp } = FiIcons;

const ReportsModal = ({ onClose }) => {
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch leads
      const { data: leadsData, error: leadsError } = await supabase
        .from('leads')
        .select('*')
        .gte('created_at', `${dateRange.start}T00:00:00`)
        .lte('created_at', `${dateRange.end}T23:59:59`)
        .order('created_at', { ascending: false });

      if (leadsError) throw leadsError;

      // Fetch contacts with lead information
      const { data: contactsData, error: contactsError } = await supabase
        .from('contacts')
        .select(`
          *,
          leads (
            name,
            company_name,
            phone,
            email,
            service_requested,
            lead_source,
            status
          )
        `)
        .gte('contact_date', `${dateRange.start}T00:00:00`)
        .lte('contact_date', `${dateRange.end}T23:59:59`)
        .order('contact_date', { ascending: false });

      if (contactsError) throw contactsError;

      setLeads(leadsData || []);
      setContacts(contactsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportLeadsToCSV = () => {
    const data = leads.map(lead => ({
      'Name': lead.name,
      'Company': lead.company_name || '',
      'Phone': lead.phone,
      'Email': lead.email || '',
      'Website': lead.website || '',
      'Service Requested': lead.service_requested || '',
      'Lead Source': lead.lead_source || '',
      'Status': lead.status,
      'Notes': lead.notes || '',
      'AI Reference Notes': lead.ai_reference_notes || '',
      'Created Date': format(new Date(lead.created_at), 'yyyy-MM-dd HH:mm:ss'),
      'Updated Date': format(new Date(lead.updated_at), 'yyyy-MM-dd HH:mm:ss')
    }));

    const csv = Papa.unparse(data);
    downloadCSV(csv, `leads_export_${format(new Date(), 'yyyy-MM-dd')}.csv`);
  };

  const exportContactsToCSV = () => {
    const data = contacts.map(contact => ({
      'Contact Date': format(new Date(contact.contact_date), 'yyyy-MM-dd HH:mm:ss'),
      'Contact Type': contact.contact_type === 'phone' ? 'Phone Call' : contact.contact_type === 'email' ? 'Email' : 'SMS',
      'Subject': contact.subject || '',
      'Content': contact.content || '',
      'Notes': contact.notes || '',
      'Lead Name': contact.leads?.name || '',
      'Lead Company': contact.leads?.company_name || '', // Added Company Name
      'Lead Phone': contact.leads?.phone || '',
      'Lead Email': contact.leads?.email || '',
      'Lead Service Requested': contact.leads?.service_requested || '',
      'Lead Source': contact.leads?.lead_source || '',
      'Lead Status': contact.leads?.status || '',
      'Contact Created': format(new Date(contact.created_at), 'yyyy-MM-dd HH:mm:ss')
    }));

    const csv = Papa.unparse(data);
    downloadCSV(csv, `contact_log_export_${format(new Date(), 'yyyy-MM-dd')}.csv`);
  };

  // Calculate statistics
  const stats = {
    totalLeads: leads.length,
    newLeads: leads.filter(l => l.status === 'New').length,
    contactedLeads: leads.filter(l => l.status === 'Contacted').length,
    scheduledLeads: leads.filter(l => l.status === 'Scheduled').length,
    completedLeads: leads.filter(l => l.status === 'Completed').length,
    totalContacts: contacts.length,
    phoneContacts: contacts.filter(c => c.contact_type === 'phone').length,
    emailContacts: contacts.filter(c => c.contact_type === 'email').length,
    smsContacts: contacts.filter(c => c.contact_type === 'sms').length
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Reports & Analytics</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiX} className="h-6 w-6" />
            </button>
          </div>

          {/* Date Range Filter */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Date Range</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-electric-600"></div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Statistics */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{stats.totalLeads}</div>
                    <div className="text-sm text-blue-700">Total Leads</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-yellow-600">{stats.newLeads}</div>
                    <div className="text-sm text-yellow-700">New Leads</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{stats.completedLeads}</div>
                    <div className="text-sm text-green-700">Completed</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-purple-600">{stats.totalContacts}</div>
                    <div className="text-sm text-purple-700">Total Contact Logs</div>
                  </div>
                </div>
              </div>

              {/* Contact Log Statistics */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Log Statistics</h3>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-orange-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">{stats.phoneContacts}</div>
                    <div className="text-sm text-orange-700">Phone Calls</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-red-600">{stats.emailContacts}</div>
                    <div className="text-sm text-red-700">Emails</div>
                  </div>
                  <div className="bg-indigo-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-indigo-600">{stats.smsContacts}</div>
                    <div className="text-sm text-indigo-700">SMS Messages</div>
                  </div>
                </div>
              </div>

              {/* Export Options */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Export Data</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={exportLeadsToCSV}
                    className="flex items-center justify-center space-x-2 px-6 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <SafeIcon icon={FiDownload} className="h-5 w-5" />
                    <span>Export Leads to CSV</span>
                  </button>
                  <button
                    onClick={exportContactsToCSV}
                    className="flex items-center justify-center space-x-2 px-6 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <SafeIcon icon={FiDownload} className="h-5 w-5" />
                    <span>Export Contact Logs to CSV</span>
                  </button>
                </div>
              </div>

              {/* Lead Status Breakdown */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Lead Status Breakdown</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>New:</span>
                      <span className="font-medium">{stats.newLeads} ({stats.totalLeads > 0 ? Math.round((stats.newLeads / stats.totalLeads) * 100) : 0}%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Contacted:</span>
                      <span className="font-medium">{stats.contactedLeads} ({stats.totalLeads > 0 ? Math.round((stats.contactedLeads / stats.totalLeads) * 100) : 0}%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Scheduled:</span>
                      <span className="font-medium">{stats.scheduledLeads} ({stats.totalLeads > 0 ? Math.round((stats.scheduledLeads / stats.totalLeads) * 100) : 0}%)</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Completed:</span>
                      <span className="font-medium">{stats.completedLeads} ({stats.totalLeads > 0 ? Math.round((stats.completedLeads / stats.totalLeads) * 100) : 0}%)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsModal;